import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import {
  FiHome,
  FiLayers,
  FiMapPin,
  FiMessageSquare,
  FiMove,
  FiPlusSquare,
  FiSliders,
  FiUpload
} from 'react-icons/fi';
import { FaMapMarkedAlt, FaProjectDiagram } from 'react-icons/fa';
import { BiCurrentLocation } from 'react-icons/bi';
import type { Box, BoxDraft } from '../../types';
import { TYPE_OPTIONS } from '../../lib/boxes';
import { GOOGLE_MAPS_KEY, parseLatLng, searchGoogleAddress, type LatLng } from '../../lib/geocode';
import { Button, Select, Spinner } from '../ui';
import { cn } from '../../lib/cn';
import BoxModal from '../BoxModal';
import { DraggableMarker, MapClickHandler, Recenter, addressIcon, meIcon } from './MapBits';

interface NavigationPageProps {
  boxes: Box[];
  popNames: string[];
  uniquePops: string[];
  isAdmin: boolean;
  userLocation: LatLng | null;
  boxesLoading: boolean;
  onSaveBox: (payload: BoxDraft) => Promise<void>;
  onCreateBox: (payload: BoxDraft) => Promise<void>;
  onDeleteBox: (id: string) => Promise<void>;
  onImportCsv: (file: File) => Promise<void>;
}

const INITIAL_CENTER: LatLng = [-23.473557, -47.495665];

export default function NavigationPage({
  boxes,
  popNames,
  uniquePops,
  isAdmin,
  userLocation,
  boxesLoading,
  onSaveBox,
  onCreateBox,
  onDeleteBox,
  onImportCsv
}: NavigationPageProps) {
  const [navPop, setNavPop] = useState('');
  const [navStatusFilter, setNavStatusFilter] = useState<string | null>(null);
  const [navNameSearch, setNavNameSearch] = useState('');
  const [navDateFrom, setNavDateFrom] = useState('');
  const [navDateTo, setNavDateTo] = useState('');
  const [navFiltersOpen, setNavFiltersOpen] = useState(true);
  const [mapToolbarOpen, setMapToolbarOpen] = useState(false);
  const [creatingBox, setCreatingBox] = useState(false);
  const [newBoxDraft, setNewBoxDraft] = useState<BoxDraft | null>(null);
  const [movingBox, setMovingBox] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressPin, setAddressPin] = useState<{ latlng: LatLng; label: string } | null>(null);
  const [center, setCenter] = useState<LatLng | null>(null);
  const [selectedBox, setSelectedBox] = useState<BoxDraft | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const navBoxesByPop = useMemo(() => {
    if (!navPop) return [];
    if (navPop === 'ALL') return boxes;
    return boxes.filter((b) => (b.pop || '').trim() === navPop);
  }, [boxes, navPop]);

  const navBoxes = useMemo(() => {
    const search = navNameSearch.trim().toLowerCase();
    return navBoxesByPop.filter((b) => {
      const statusOk = !navStatusFilter || String(b.status || '').trim() === navStatusFilter;
      const date = b.LastEditDate ? new Date(b.LastEditDate) : null;
      const fromOk = !navDateFrom || (date && date >= new Date(navDateFrom + 'T00:00:00'));
      const toOk = !navDateTo || (date && date <= new Date(navDateTo + 'T23:59:59'));
      const nameOk = !search || String(b.name || '').toLowerCase().includes(search);
      return statusOk && fromOk && toOk && nameOk;
    });
  }, [navBoxesByPop, navStatusFilter, navDateFrom, navDateTo, navNameSearch]);

  const totalBoxes = navBoxesByPop.length;
  const countMontada = useMemo(
    () => navBoxesByPop.filter((b) => String(b.status || '').trim() === 'montada').length,
    [navBoxesByPop]
  );
  const countSemSinal = useMemo(
    () => navBoxesByPop.filter((b) => String(b.status || '').trim() === 'montada s/ sinal').length,
    [navBoxesByPop]
  );
  const mountedBoxes = countMontada + countSemSinal;
  const mountedPct = totalBoxes > 0 ? ((mountedBoxes / totalBoxes) * 100).toFixed(1) : '0.0';

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && creatingBox) setCreatingBox(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [creatingBox]);

  const geocodeAddress = async () => {
    const query = addressSearch.trim();
    if (!query) return;

    const coords = parseLatLng(query);
    if (coords) {
      setAddressPin({ latlng: coords, label: `${coords[0]}, ${coords[1]}` });
      setCenter(coords);
      return;
    }

    if (!GOOGLE_MAPS_KEY) {
      window.alert('Chave do Google Maps não configurada.\nAdicione VITE_GOOGLE_MAPS_KEY no arquivo .env');
      return;
    }
    setAddressLoading(true);
    try {
      const hit = await searchGoogleAddress(query);
      if (!hit) {
        window.alert(
          'Endereço não encontrado nos municípios atendidos.\n\nMunicípios: Sorocaba, Capela do Alto, Iperó, Boituva, Cerquilho, Tietê.\n\nDica: informe só o nome da rua e número, ex: "Rua Dalva de Oliveira, 29".'
        );
        return;
      }
      setAddressPin(hit);
      setCenter(hit.latlng);
    } catch {
      window.alert('Erro ao buscar endereço. Verifique sua conexão.');
    } finally {
      setAddressLoading(false);
    }
  };

  const statusKpi = (label: string, value: number, key: string, color: string) => (
    <button
      onClick={() => setNavStatusFilter((f) => (f === key ? null : key))}
      className={cn(
        'flex min-w-[92px] flex-col rounded-xl border px-3 py-2 text-left transition',
        navStatusFilter === key ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-500/20' : 'border-slate-200 bg-white hover:border-slate-300'
      )}
      title={`Filtrar: ${label}`}
    >
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <span className={cn('text-xl font-bold', color)}>{value}</span>
    </button>
  );

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      {/* Barra de filtros / KPIs */}
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-white px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-600">
              <FiHome size={16} />
            </span>
            <Select value={navPop} onChange={(e) => setNavPop(e.target.value)} className="w-48">
              {uniquePops.map((pop) => (
                <option key={pop} value={pop}>
                  {pop === '' ? 'Selecione o POP' : pop}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  title="id;nome;type;splitter;pop;latitude;longitude"
                  onClick={() => fileRef.current?.click()}
                >
                  <FiUpload size={16} />
                  <span className="hidden md:inline">Importar CSV</span>
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onImportCsv(f).catch((err) => window.alert((err as Error).message || 'Erro CSV'));
                  }}
                />
              </>
            )}

            <Button
              variant="ghost"
              size="sm"
              disabled={!userLocation}
              onClick={() => userLocation && setCenter(userLocation)}
              title="Minha localização"
            >
              <BiCurrentLocation size={16} />
            </Button>

            <Button variant="ghost" size="sm" onClick={() => setNavFiltersOpen((v) => !v)}>
              <FiSliders size={14} />
              <span>{navFiltersOpen ? 'Ocultar filtros' : 'Filtros'}</span>
            </Button>

            <Button variant={mapToolbarOpen ? 'primary' : 'ghost'} size="sm" onClick={() => setMapToolbarOpen((v) => !v)}>
              <FiLayers size={16} />
              <span className="hidden md:inline">Ferramentas</span>
            </Button>

            <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
              <FaMapMarkedAlt size={14} />
              <span>
                {navBoxes.length}
                {navBoxes.length !== boxes.length ? ` / ${boxes.length}` : ''} caixas
              </span>
              {boxesLoading && <Spinner className="h-3 w-3 text-slate-400" />}
            </div>
          </div>
        </div>

        {navFiltersOpen && (
          <div className="flex flex-wrap items-stretch gap-2">
            {statusKpi('Montadas', countMontada, 'montada', 'text-emerald-600')}
            {statusKpi('S/ sinal', countSemSinal, 'montada s/ sinal', 'text-amber-600')}
            <div className="flex min-w-[80px] flex-col rounded-xl border border-slate-200 bg-white px-3 py-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Total</span>
              <span className="text-xl font-bold text-slate-700">{totalBoxes}</span>
            </div>
            <div className="flex min-w-[90px] flex-col rounded-xl border border-slate-200 bg-white px-3 py-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Progresso</span>
              <span className="text-xl font-bold text-blue-600">{mountedPct}%</span>
            </div>

            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
              <label className="flex flex-col text-[11px] font-medium uppercase tracking-wide text-slate-400">
                De
                <input
                  type="date"
                  className="mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 outline-none focus:border-blue-500"
                  value={navDateFrom}
                  onChange={(e) => setNavDateFrom(e.target.value)}
                />
              </label>
              <label className="flex flex-col text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Até
                <input
                  type="date"
                  className="mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 outline-none focus:border-blue-500"
                  value={navDateTo}
                  onChange={(e) => setNavDateTo(e.target.value)}
                />
              </label>
              {(navDateFrom || navDateTo) && (
                <button
                  className="self-end text-xs font-medium text-blue-600 hover:underline"
                  onClick={() => {
                    setNavDateFrom('');
                    setNavDateTo('');
                  }}
                >
                  limpar
                </button>
              )}
            </div>

            <div className="relative flex min-w-[200px] flex-1 items-center">
              <FaProjectDiagram size={14} className="pointer-events-none absolute left-3 text-slate-400" />
              <input
                type="text"
                className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-8 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Id da caixa..."
                value={navNameSearch}
                onChange={(e) => setNavNameSearch(e.target.value)}
              />
              {navNameSearch && (
                <button className="absolute right-2 text-slate-400 hover:text-slate-600" onClick={() => setNavNameSearch('')}>
                  ✕
                </button>
              )}
            </div>

            <div className="relative flex min-w-[220px] flex-1 items-center">
              <FiMapPin size={14} className="pointer-events-none absolute left-3 text-slate-400" />
              <input
                type="text"
                className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-20 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Buscar endereço..."
                value={addressSearch}
                onChange={(e) => setAddressSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && geocodeAddress()}
                disabled={addressLoading}
              />
              <button
                className="absolute right-2 flex h-7 items-center rounded-lg bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={geocodeAddress}
                disabled={addressLoading || !addressSearch.trim()}
                title="Buscar endereço no mapa"
              >
                {addressLoading ? <Spinner className="h-3.5 w-3.5" /> : 'Ir'}
              </button>
              {addressPin && (
                <button
                  className="absolute right-12 text-slate-400 hover:text-slate-600"
                  onClick={() => {
                    setAddressPin(null);
                    setAddressSearch('');
                  }}
                  title="Limpar pin de endereço"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mapa */}
      <div className="relative min-h-0 flex-1">
        {mapToolbarOpen && (
          <div className="absolute left-1/2 top-3 z-[1000] flex -translate-x-1/2 flex-row items-center gap-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
            <button
              className={cn(
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition',
                creatingBox ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              )}
              title={creatingBox ? 'Clique no mapa para posicionar (ESC para cancelar)' : 'Criar nova caixa no mapa'}
              onClick={() => {
                setCreatingBox((v) => !v);
                setMovingBox(false);
              }}
            >
              <FiPlusSquare size={16} />
              <span>{creatingBox ? 'Clique no mapa...' : 'Criar caixa'}</span>
            </button>
            <button
              className={cn(
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition',
                movingBox ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              )}
              title={movingBox ? 'Modo mover ativo — clique para sair' : 'Mover caixa no mapa'}
              onClick={() => {
                setMovingBox((v) => !v);
                setCreatingBox(false);
              }}
            >
              <FiMove size={16} />
              <span>{movingBox ? 'Movendo...' : 'Mover caixa'}</span>
            </button>
            <button className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100" title="Adicionar anotação no mapa">
              <FiMessageSquare size={16} />
              <span>Criar anotação</span>
            </button>
          </div>
        )}

        <MapContainer center={INITIAL_CENTER} zoom={16} className="h-full w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapClickHandler
            active={creatingBox}
            movingMode={movingBox}
            onMapClick={(latlng) => {
              setNewBoxDraft({
                id: '',
                name: '',
                type: TYPE_OPTIONS[0],
                splitter: '',
                pop: '',
                latitude: latlng.lat,
                longitude: latlng.lng,
                signal: '',
                status: '',
                images: []
              });
              setCreatingBox(false);
            }}
          />
          {userLocation && <Marker position={userLocation} icon={meIcon()} />}
          {addressPin && (
            <Marker position={addressPin.latlng} icon={addressIcon()}>
              <Popup>
                <div className="max-w-[220px] text-[13px]">{addressPin.label}</div>
              </Popup>
            </Marker>
          )}
          {navBoxes.map((box) => (
            <DraggableMarker
              key={box.id}
              box={box}
              movingMode={movingBox}
              onDragEnd={(newLatLng) => {
                const updated: BoxDraft = { ...box, originalId: box.id, latitude: newLatLng.lat, longitude: newLatLng.lng };
                onSaveBox(updated).catch((err) => window.alert((err as Error).message || 'Erro ao mover caixa'));
              }}
              onClick={() => {
                if (!movingBox) setSelectedBox({ ...box, originalId: box.id });
              }}
            />
          ))}
          <Recenter center={center} />
        </MapContainer>
      </div>

      {selectedBox && (
        <BoxModal
          box={selectedBox}
          isAdmin={isAdmin}
          popOptions={popNames}
          onClose={() => setSelectedBox(null)}
          onSave={(payload) =>
            onSaveBox(payload)
              .then(() => setSelectedBox(null))
              .catch((err) => window.alert((err as Error).message || 'Erro ao salvar'))
          }
          onDelete={(id) =>
            onDeleteBox(id)
              .then(() => setSelectedBox(null))
              .catch((err) => window.alert((err as Error).message || 'Erro ao excluir'))
          }
        />
      )}

      {newBoxDraft && (
        <BoxModal
          box={newBoxDraft}
          isAdmin
          popOptions={popNames}
          onClose={() => setNewBoxDraft(null)}
          onSave={(payload) =>
            onCreateBox(payload)
              .then(() => setNewBoxDraft(null))
              .catch((err) => window.alert((err as Error).message || 'Erro ao criar caixa'))
          }
        />
      )}
    </section>
  );
}
